/* Mission Control HUD (Theme settings, Effects): where you are on a long page.
   Waypoints come from sections that have a "Section rail name" ([data-mc-waypoint]); pages with fewer than three
   get no HUD. Desktop (990px and up): a slim rail on the left edge, a tick per waypoint, a fill that reaches each
   tick as its section arrives, and a live readout (waypoint and page coordinate). Phones and tablets: a 2px
   progress line on the header's lower edge and the current waypoint's name in the header bar, which opens the
   list of waypoints. A real nav landmark: links, aria-current="location", and a polite announcement when a flight
   from the HUD lands. Selecting a waypoint flies there (jumps under reduced motion) and moves focus to it.
   Scroll work is one rAF-coalesced read of scrollY against cached positions; no layout is read while scrolling. */
(() => {
  const root = document.documentElement;
  if (!(root.getAttribute('data-fx') || '').split(' ').includes('hud')) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desk = window.matchMedia('(min-width: 990px)');

  function init() {
    if (document.querySelector('.mc-hud')) return;
    const secs = [...document.querySelectorAll('#MainContent [data-mc-waypoint]')].filter((s) => s.offsetParent !== null || s.getClientRects().length);
    if (secs.length < 3) return;
    const pad = (n) => String(n).padStart(2, '0');
    // A name written all in lowercase keeps its case (the way is always lowercase); others set as placards
    const keepCase = (t) => t === t.toLowerCase() && /[a-z]/.test(t);
    const total = pad(secs.length);

    const nav = document.createElement('nav');
    nav.className = 'mc-hud';
    nav.setAttribute('aria-label', 'Sections on this page');
    nav.innerHTML = `
      <button class="mc-hud__now" type="button" aria-expanded="false" aria-controls="McHudList">
        <span class="mc-hud__now-idx" aria-hidden="true"></span>
        <span class="mc-hud__now-name"></span>
        <svg class="mc-hud__chev" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>
      </button>
      <span class="mc-hud__line" aria-hidden="true"><i></i></span>
      <p class="mc-hud__readout mc-hud__readout--top" aria-hidden="true"><span class="mc-hud__idx"></span><span class="mc-hud__name"></span></p>
      <div class="mc-hud__rail">
        <span class="mc-hud__track" aria-hidden="true"><i></i></span>
        <ol class="mc-hud__list" id="McHudList"></ol>
      </div>
      <p class="mc-hud__readout mc-hud__readout--bottom" aria-hidden="true"><span class="mc-hud__axis">Y</span><span class="mc-hud__coord">0.000</span></p>
      <p class="mc-visually-hidden" aria-live="polite" aria-atomic="true"></p>`;
    const list = nav.querySelector('ol');
    const links = secs.map((s, i) => {
      const host = s.closest('.shopify-section') || s;
      const id = s.id || host.id;
      const li = document.createElement('li');
      li.style.setProperty('--y', secs.length > 1 ? (i / (secs.length - 1)).toFixed(4) : '0');
      const a = document.createElement('a');
      a.className = 'mc-hud__wp';
      a.href = `#${id}`;
      a.innerHTML = '<span class="mc-hud__node" aria-hidden="true"></span><span class="mc-hud__label"></span>';
      a.querySelector('.mc-hud__label').textContent = s.dataset.mcWaypoint;
      if (keepCase(s.dataset.mcWaypoint)) a.classList.add('is-keep-case');
      li.appendChild(a);
      list.appendChild(li);
      return a;
    });
    const main = document.getElementById('MainContent');
    main.parentNode.insertBefore(nav, main);

    const btn = nav.querySelector('.mc-hud__now');
    const nowIdx = nav.querySelector('.mc-hud__now-idx');
    const nowName = nav.querySelector('.mc-hud__now-name');
    const idx = nav.querySelector('.mc-hud__idx');
    const name = nav.querySelector('.mc-hud__name');
    const coord = nav.querySelector('.mc-hud__coord');
    const fill = nav.querySelector('.mc-hud__track i');
    const line = nav.querySelector('.mc-hud__line i');
    const live = nav.querySelector('[aria-live]');
    const header = document.querySelector('[data-mc-header]');

    /* Positions, cached: section tops in page coordinates, re-measured on resize and when the page's height changes */
    let tops = [], range = 1, vh = window.innerHeight;
    const measure = () => {
      const y = sy = window.scrollY;
      vh = window.innerHeight;
      tops = secs.map((s) => s.getBoundingClientRect().top + y);
      range = Math.max(1, document.documentElement.scrollHeight - vh);
      fitButton();
    };
    // Phones: the name sits between the wordmark and the menu button, never over either
    function fitButton() {
      if (desk.matches || !header) return;
      const mark = header.querySelector('.mc-header__mark');
      const toggle = header.querySelector('.mc-header__toggle');
      if (!mark || !toggle) return;
      const m = mark.getBoundingClientRect(), t = toggle.getBoundingClientRect();
      // The menu button's icon sits inside a 44px target, so the name can run up to the target's edge
      const room = t.left - m.right - 12;
      nav.style.setProperty('--hud-right', `${Math.round(window.innerWidth - t.left)}px`);
      nav.style.setProperty('--hud-room', `${Math.max(0, Math.round(room))}px`);
      nav.classList.toggle('is-tight', room < 170);
      nav.classList.toggle('is-cramped', room < 76);
    }

    let cur = -1, ticking = false, landed = 0, sy = window.scrollY, past = null;
    function update() {
      ticking = false;
      const y = sy;
      const reading = y + vh * 0.38;
      let c = 0;
      for (let i = 0; i < tops.length; i++) if (tops[i] <= reading) c = i;
      if (y >= range - 2) c = tops.length - 1; // the page's end belongs to its last waypoint
      // The fill reaches each tick as its section arrives at the reading line
      const a = tops[c], b = c + 1 < tops.length ? tops[c + 1] : Math.max(a + 1, range + vh * 0.38);
      const seg = Math.min(1, Math.max(0, (reading - a) / Math.max(1, b - a)));
      const p = secs.length > 1 ? Math.min(1, (c + (c + 1 < tops.length ? seg : 0)) / (secs.length - 1)) : 1;
      fill.style.transform = `scaleY(${p.toFixed(4)})`;
      line.style.transform = `scaleX(${Math.min(1, y / range).toFixed(4)})`;
      if (!reduced && desk.matches) coord.textContent = Math.min(1, y / range).toFixed(3);
      if (past !== y > vh * 0.5) { past = y > vh * 0.5; nav.classList.toggle('is-past-top', past); }
      if (c !== cur) setCurrent(c);
    }
    function setCurrent(c) {
      cur = c;
      links.forEach((l, i) => {
        if (i === c) l.setAttribute('aria-current', 'location'); else l.removeAttribute('aria-current');
        l.parentNode.classList.toggle('is-past', i < c);
      });
      const label = secs[c].dataset.mcWaypoint;
      idx.textContent = `${pad(c + 1)} / ${total}`;
      nowIdx.textContent = `${pad(c + 1)}/${total}`;
      name.textContent = label;
      nowName.textContent = label;
      nav.classList.toggle('is-keep-case', keepCase(label));
      btn.setAttribute('aria-label', `Section ${c + 1} of ${secs.length}: ${label}. Show all sections`);
      if (reduced) coord.textContent = Math.min(1, sy / range).toFixed(3);
      // aria-current carries the position; the live region speaks only when a flight from the HUD lands, so it
      // never interrupts someone reading (and scrolling) through the page
    }
    // scrollY is read in the scroll event (layout is already current there), so the frame callback only writes
    const onScroll = () => { sy = window.scrollY; if (!ticking) { ticking = true; requestAnimationFrame(update); } if (open) setOpen(false); };

    /* Flights */
    let open = false;
    function setOpen(v) {
      open = v;
      btn.setAttribute('aria-expanded', String(v));
      nav.classList.toggle('is-open', v);
    }
    btn.addEventListener('click', () => {
      setOpen(!open);
      if (open) (links[cur] || links[0]).focus({ preventScroll: true });
    });
    nav.addEventListener('keydown', (e) => { if (e.key === 'Escape' && open) { setOpen(false); btn.focus(); } });
    // Focus leaving the list closes it (keyboard), but not a tap inside it: Safari does not focus a tapped link, so the
    // tap blurs the current one with nowhere to go, and closing then would swallow the tap's click
    // (iOS blurs at the compatibility mousedown, after the finger lifts, so the hold lasts a moment past release)
    let pressing = false, release = 0;
    nav.addEventListener('pointerdown', () => { clearTimeout(release); pressing = true; });
    const lift = () => { clearTimeout(release); release = setTimeout(() => { pressing = false; }, 500); };
    nav.addEventListener('pointerup', lift);
    nav.addEventListener('pointercancel', lift);
    nav.addEventListener('focusout', (e) => {
      if (!open || pressing || nav.contains(e.relatedTarget)) return;
      setOpen(false);
    });
    document.addEventListener('click', (e) => { if (open && !nav.contains(e.target)) setOpen(false); });
    links.forEach((a, i) => a.addEventListener('click', (e) => {
      const target = secs[i];
      e.preventDefault();
      setOpen(false);
      // Quiet while flying past the sections in between; the destination is announced on arrival
      clearTimeout(landed);
      landed = setTimeout(() => { live.textContent = `Now viewing ${target.dataset.mcWaypoint}`; }, reduced ? 50 : 1200);
      target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      // Only the sections' own anchors go in the address bar, never the template-bound wrapper ids
      const hash = a.getAttribute('href');
      if (history.replaceState && !hash.startsWith('#shopify-section-')) history.replaceState(null, '', hash);
    }));

    /* Never in the way: hidden while the footer is on screen and while the phone menu is open */
    const footer = document.querySelector('.shopify-section-group-footer-group');
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver((en) => nav.classList.toggle('is-docked', en[en.length - 1].isIntersecting), { rootMargin: '0px 0px -12% 0px' }).observe(footer);
    }
    if (header) new MutationObserver(() => { nav.classList.toggle('is-menu', header.classList.contains('is-open')); if (header.classList.contains('is-open')) setOpen(false); }).observe(header, { attributes: true, attributeFilter: ['class'] });
    // The way portal turns the HUD's accent to lantern orange while it fills the screen (world colours)
    const ways = [...document.querySelectorAll('[data-mc-way-section]')];
    if (ways.length && root.hasAttribute('data-world') && 'IntersectionObserver' in window) {
      const wio = new IntersectionObserver((en) => { en.forEach((e) => { e.target.__hud = e.isIntersecting; }); nav.classList.toggle('is-way', ways.some((w) => w.__hud)); }, { rootMargin: '-30% 0px -30% 0px' });
      ways.forEach((w) => wio.observe(w));
    }

    measure();
    update();
    nav.classList.add('is-ready');
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => { measure(); update(); });
    desk.addEventListener('change', () => { setOpen(false); measure(); update(); });
    if ('ResizeObserver' in window) {
      let pending = 0;
      new ResizeObserver(() => { if (pending) return; pending = requestAnimationFrame(() => { pending = 0; measure(); update(); }); }).observe(document.body);
    }
    document.fonts?.ready.then(() => { measure(); update(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
